import { Newtype } from 'newtype-ts';
import { InternationalizeString, InternationalizeStringOpt, Language } from '@shared/modules/translation/model';
import { Resource } from '@shared/modules/resources/model';
import { z } from 'zod';

export type DiagnostictId = Newtype<{ readonly ID: unique symbol }, string> & string;
export type StepId = Newtype<{ readonly ID: unique symbol }, string> & string;
export type DocumentsId = Newtype<{ readonly ID: unique symbol }, string> & string;
export type ImagesId = Newtype<{ readonly ID: unique symbol }, string> & string;
export type AnswersId = Newtype<{ readonly ID: unique symbol }, string> & string;

export interface Diagnostic {
  id: DiagnostictId;
  number: number;
  name: InternationalizeString;
  product: string;
  isEnglishAllowed: boolean;
  initialStepId: StepId;
  image: Resource;
}

export interface DiagnosticsStepDocuments {
  id: DocumentsId;
  name: string;
  shortDescription: string | null;
  resource: Resource;
  language: Language;
}

export interface DiagnosticsStepImages {
  id: ImagesId;
  resource: Resource;
  language: Language;
}

export enum DiagnosticsAnswerType {
  Solved = 'solved',
  Breakdown = 'breakdown',
  Continue = 'continue',
}

export enum DiagnosticsBreakdownType {
  Mechanical = 'mechanical',
  Electronic = 'electronic',
  Ui = 'ui',
}

export interface DiagnosticsStepAnswers {
  id: AnswersId;
  label: InternationalizeString;
  answerType: DiagnosticsAnswerType;
  breakdownType: DiagnosticsBreakdownType;
  nextStepId: string;
}

export interface DiagnosticsStepDetail {
  id: StepId;
  isRoot: boolean;
  shortName: InternationalizeString;
  detail: InternationalizeStringOpt;
  videoUrl: InternationalizeString;
  documents: Array<DiagnosticsStepDocuments>;
  images: Array<DiagnosticsStepImages>;
  questionTitle: InternationalizeStringOpt | null;
  answers: Array<DiagnosticsStepAnswers>;
}

export const diagnosticsResolvedSchema = z.object({
  answerId: z.string().min(1),
  score: z.number(),
  comment: z.string(),
});

export type DiagnosticsResolved = z.infer<typeof diagnosticsResolvedSchema>;
